import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import { mergeProps, useObjectRef } from 'react-aria';

import {
    type ButtonHookProps,
    type ButtonResult,
    type GenericButtonHookProps,
    useButton,
} from '../Button';
import {
    type CardHookProps,
    CardProvider,
    type CardResult,
    filterCardDOMProps,
    type GenericCardHookProps,
    useCard,
} from './Card';
import { CardDismissButton } from './CardDismissButton';
import { usePressableCardKeyboardNavigation } from './usePressableCardKeyboardNavigation';

export type GenericCardButtonHookProps = GenericButtonHookProps & GenericCardHookProps;
export type CardButtonHookProps = ButtonHookProps & CardHookProps;
export type CardButtonResult = ButtonResult['buttonProps'] & CardResult;

export type CardButtonProps = GenericCardButtonHookProps & React.ComponentPropsWithoutRef<'div'>;

/**
 * Card button component to use a pressable card.
 */
export function CardButton({
    ref,
    disabled,
    onPress,
    preventFocusOnPress,
    onPressStart,
    className,
    onDismiss,
    kind,
    ...props
}: CardButtonProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const rootRef = useObjectRef(ref);
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const domProps = filterCardDOMProps(props);
    const keyboardNavigationProps = usePressableCardKeyboardNavigation(rootRef);
    const cardButtonProps = useCardButton({
        elementType: 'div',
        onPress,
        onPressStart,
        preventFocusOnPress,
        disabled,
        className,
        kind,
        headingId,
        descriptionId,
    });

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            <div ref={rootRef} {...mergeProps(domProps, cardButtonProps, keyboardNavigationProps)}>
                {onDismiss ? <CardDismissButton onDismiss={onDismiss} /> : null}
                {props.children}
            </div>
        </CardProvider>
    );
}

/**
 * Low-level hook to create a card button component.
 */
export function useCardButton(props: CardButtonHookProps): CardButtonResult {
    const { className } = useCard(props);
    const { buttonProps, isDisabled, isFocusVisible } = useButton(props);

    return {
        ...buttonProps,
        className: clsx(
            className,
            isDisabled
                ? 'opacity-disabled'
                : clsx(
                      'group/card-pressable cursor-pointer transition-shadow ease-in-out',
                      props.kind !== 'ghost' && 'hover:bg-neutral-surface-subtle',
                      props.kind !== 'ghost' && isFocusVisible && 'focus-ring'
                  )
        ),
    };
}
